import { currentLocale } from '@web/i18n';

/**
 * Formats a dossier date (Zod `z.coerce.date()`) in the active UI locale, or a
 * dash when absent. Shared by the customer screen and the alerts panel's traveler
 * data (both render the same `CustomerProfilePanel`).
 *
 * Formatted in **UTC**: trip/voucher dates are calendar-date instants stored as
 * UTC midnight (the server's trip-name builder formats them in UTC too). Using
 * the browser TZ would shift a UTC-midnight date back a day in negative offsets
 * (e.g. BRT), so the range would disagree with the trip title by one day.
 */
export function formatDate(value: Date | null | undefined): string {
  // Foreign sources emit the unix epoch (01/01/1970) as a "no date" sentinel —
  // treat it as absent so the screen never renders the 1970 eyesore.
  if (!value || value.getTime() === 0) return '—';
  return value.toLocaleDateString(currentLocale(), { timeZone: 'UTC' });
}

/**
 * Like {@link formatDate} but returns `null` for an absent or epoch date, so the
 * caller can render a labelled-empty text ("Não informado") instead of a dash.
 */
export function formatDateOrNull(value: Date | null | undefined): string | null {
  if (!value || value.getTime() === 0) return null;
  return formatDate(value);
}

const ISO_DATE_HEAD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Renders a displayed travel day from the RAW source date string, preferring it
 * over the parsed Date so the wall-clock the producer emitted survives intact.
 * The source mixes formats (local-as-`Z`, `-03:00`, `+00:00`); parsing any of
 * them through `new Date()` would re-interpret the instant and shift the day in
 * a negative-offset browser TZ. So we DO NOT parse: take the ISO date head
 * `raw.slice(0, 10)` (`YYYY-MM-DD`) and string-reformat to `DD/MM/YYYY`.
 *
 * The manual reformat is intentionally NOT `Intl`/locale-aware — `Intl` needs a
 * `Date`, which is forbidden on this path. The app renders pt-BR `DD/MM/YYYY`,
 * which the manual format equals for the dominant local-as-`Z` case (so this is
 * a no-regression swap of {@link formatDate}'s UTC output for those values).
 *
 * Falls back to `formatDate(parsed)` (the UTC formatter, reused not reimplemented)
 * when `raw` is null/absent OR its head fails `^\d{4}-\d{2}-\d{2}$` — existing
 * data is raw-null until the Phase 4 re-backfill, so the UTC path stays the
 * fallback (its full removal is Phase 4).
 */
export function formatRawDate(
  raw: string | null | undefined,
  parsed: Date | null | undefined,
): string {
  if (raw) {
    const head = raw.slice(0, 10);
    if (ISO_DATE_HEAD.test(head)) {
      return head.split('-').reverse().join('/');
    }
  }
  return formatDate(parsed);
}

const ISO_DATETIME_HEAD = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Renders a displayed travel instant (date + time) from the RAW source string,
 * for segment depart/arrive times where the wall-clock minute matters. Same
 * no-parse principle as {@link formatRawDate}: the source mixes formats
 * (local-as-`Z`, `-03:00`, `+00:00`), so parsing through `new Date()` would
 * shift the instant in a negative-offset browser TZ. We DO NOT parse — when
 * `raw` matches `^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}` we string-reformat its head to
 * `DD/MM/YYYY HH:mm`. Otherwise we fall back to {@link formatRawDate} (which
 * itself falls back to the UTC {@link formatDate}).
 */
export function formatRawDateTime(
  raw: string | null | undefined,
  parsed: Date | null | undefined,
): string {
  if (raw && ISO_DATETIME_HEAD.test(raw)) {
    const date = raw.slice(0, 10).split('-').reverse().join('/');
    const time = raw.slice(11, 16);
    return `${date} ${time}`;
  }
  return formatRawDate(raw, parsed);
}

const ISO_DURATION = /^P(?:T(?:(\d+)H)?(?:(\d+)M)?(?:\d+S)?)?$/;
const SECONDS_ONLY = /^\d+$/;

/**
 * Humanises a voucher/segment duration to `Hh MMm`. The wire value is a
 * source-formatted string: an ISO8601 duration (`PT3H`, `PT19H30M`), a legacy
 * bare-seconds integer (`68400` → `19h 00m`), or an already-human string
 * (`1h 15m`, passed through). Null/empty returns null so the caller can render
 * its own labelled-empty text.
 */
export function formatDuration(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v) return null;
  const iso = ISO_DURATION.exec(v);
  if (iso) {
    return `${String(Number(iso[1] ?? 0))}h ${String(Number(iso[2] ?? 0)).padStart(2, '0')}m`;
  }
  if (SECONDS_ONLY.test(v)) {
    const total = Number(v);
    return `${String(Math.floor(total / 3600))}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
  }
  return v;
}
